import React, { useMemo } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { getPlatformName, getPlatformColor } from '../config/platforms';
import { SectionCard } from './SectionCard';

/** The fields of a Stats post this card needs. */
export interface PerformerPost {
  platform: string;
  published_at: string;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  measured: 0 | 1;
}

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/** The weekday a post went out on, in Eastern time — the team's clock, not the server's UTC. */
const easternWeekday = (iso: string) => {
  const name = new Date(iso).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'America/New_York' });
  return WEEKDAYS.indexOf(name);
};

interface Row { key: string; label: string; color?: string; posts: number; total: number; average: number }

/**
 * Which days of the week and which networks did best, on average.
 *
 * **Organic engagement only.** Boost figures are left out on purpose: one $100 boost on a
 * Monday post would otherwise make Monday look like the best day to post, which is a statement
 * about the budget, not the day. Averages are per measured post, and every row says how many
 * posts it rests on, because a day with a single lucky post should not read as a pattern.
 */
export const BestPerformers: React.FC<{ posts: PerformerPost[]; days: number }> = ({ posts, days }) => {
  const { byDay, byNetwork } = useMemo(() => {
    const measured = posts.filter(p => p.measured === 1 && p.published_at);
    const engagement = (p: PerformerPost) => (p.likes || 0) + (p.comments || 0) + (p.shares || 0);

    const group = (keyOf: (p: PerformerPost) => string) => {
      const map = new Map<string, { posts: number; total: number }>();
      for (const p of measured) {
        const k = keyOf(p);
        const g = map.get(k) || { posts: 0, total: 0 };
        g.posts += 1;
        g.total += engagement(p);
        map.set(k, g);
      }
      return map;
    };

    const dayGroups = group(p => String(easternWeekday(p.published_at)));
    const byDay: Row[] = WEEKDAYS.map((label, i) => {
      const g = dayGroups.get(String(i)) || { posts: 0, total: 0 };
      return { key: String(i), label, posts: g.posts, total: g.total, average: g.posts ? g.total / g.posts : 0 };
    });

    const netGroups = group(p => p.platform);
    const byNetwork: Row[] = Array.from(netGroups.entries())
      .map(([k, g]) => ({
        key: k, label: getPlatformName(k), color: getPlatformColor(k),
        posts: g.posts, total: g.total, average: g.total / g.posts,
      }))
      .sort((a, b) => b.average - a.average);

    return { byDay, byNetwork };
  }, [posts]);

  if (!byNetwork.length) return null;

  const bestDay = [...byDay].filter(d => d.posts > 0).sort((a, b) => b.average - a.average)[0];
  const maxDay = Math.max(...byDay.map(d => d.average), 0.0001);
  const maxNet = Math.max(...byNetwork.map(n => n.average), 0.0001);

  const bar = (r: Row, max: number, best: boolean) => (
    <Box key={r.key} sx={{ opacity: r.posts ? 1 : 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: best ? 700 : 500, minWidth: 96 }}>{r.label}</Typography>
        <Typography variant="body2">{r.posts ? r.average.toFixed(1) : '—'}</Typography>
        <Typography variant="caption" color="text.secondary">
          {r.posts
            ? `per post · ${r.posts} post${r.posts === 1 ? '' : 's'} · ${r.total.toLocaleString()} total`
            : 'no posts'}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={(r.average / max) * 100}
        sx={{
          height: 6, borderRadius: 3, mt: 0.5,
          '& .MuiLinearProgress-bar': { backgroundColor: r.color || (best ? 'primary.main' : 'grey.500') },
        }}
      />
    </Box>
  );

  return (
    <SectionCard
      id="best-performers"
      title="What Performed Best"
      description={<>
        Average likes, comments and shares per post over the last {days} days, without boosts, so
        the answer is about the content and the timing rather than the budget.
        {bestDay && ` ${bestDay.label} did best${bestDay.posts < 3 ? ', though on only ' + bestDay.posts + ' post' + (bestDay.posts === 1 ? '' : 's') : ''}.`}
        {days < 90 && ' Choose 90 days at the top for a steadier picture.'}
      </>}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Best Days To Post</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {byDay.map(d => bar(d, maxDay, d.key === bestDay?.key))}
          </Box>
        </Box>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Best Networks</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {byNetwork.map((n, i) => bar(n, maxNet, i === 0))}
          </Box>
        </Box>
      </Box>
    </SectionCard>
  );
};
